import { isEmpty, isEqual } from 'lodash';
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, CardBody, Col, Row } from 'reactstrap';
import { CustomProperties } from 'views/Widgets/Forms/ValidatedFormContext';
import { LikertScaleInputSubItemWrapper } from 'views/Widgets/Inputs/LikertScaleInput';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';

const ValidatedInputAIWrapper: FC<
  PropsWithChildren<{
    name?: string;
    value?: unknown;
    parentName?: string;
    parentValue?: unknown;
    type: string;
    aiSuggestion: unknown;
    aiSuggestionDescription: unknown;
    singletonLikert: boolean;
    inputChangeRef?: React.RefObject<HTMLInputElement>;
    suggestionsReady: boolean;
  }>
> = ({
  children,
  value,
  name,
  parentName,
  parentValue,
  type,
  aiSuggestion,
  aiSuggestionDescription,
  singletonLikert,
  inputChangeRef,
  suggestionsReady,
}) => {
  const [show, setShow] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    if (suggestionsReady && !hasChecked) {
      setHasChecked(true);
      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(typeof value === 'object' && isEmpty(value)) &&
        aiSuggestion !== null &&
        aiSuggestion !== undefined &&
        aiSuggestion !== '' &&
        !isAISuggestionEquals(type, value, aiSuggestion)
      ) {
        setShow(true);
      }
    }
    if (!suggestionsReady && hasChecked) {
      setShow(false);
      setHasChecked(false);
    }
  }, [aiSuggestion, hasChecked, type, suggestionsReady, value]);

  const onClickAccept = useCallback(() => {
    if (inputChangeRef?.current) {
      // Only in case of Likert scale input, we need to update the parent value
      if (parentName && INPUT_TYPES.LIKERT === type) {
        if (singletonLikert) {
          // @ts-expect-error
          inputChangeRef.current({
            target: {
              name: parentName,
              value: aiSuggestion,
            },
          });
        } else {
          // @ts-expect-error
          inputChangeRef.current({
            target: {
              name: parentName,
              value: {
                // @ts-expect-error
                ...parentValue,
                // @ts-expect-error
                [name]: aiSuggestion,
              },
            },
          });
        }
      } else {
        // @ts-expect-error
        inputChangeRef.current({
          target: {
            name,
            value: aiSuggestion,
          },
        });
      }
      setShow(false);
    }
  }, [
    aiSuggestion,
    singletonLikert,
    inputChangeRef,
    name,
    parentName,
    parentValue,
    type,
  ]);
  const onClickDiscard = useCallback(() => {
    setShow(false);
  }, []);

  if (!show || aiSuggestion === undefined || aiSuggestion === null) {
    return <>{children}</>;
  }

  const aiSuggestionElement = (
    <CardBody className="alert alert-light border-1 rounded-2">
      <Row className="justify-content-start">
        <Col
          className={
            'align-self-center' +
            ([INPUT_TYPES.LIKERT].includes(type)
              ? ' d-flex align-content-start'
              : '')
          }
        >
          <b>
            <FormattedMessage
              id="app.views.a_i.form.validated_input_a_i_wrapper.ai_suggestion"
              defaultMessage="AI suggestion:"
            />
          </b>{' '}
          {suggestionFormatter(type, aiSuggestion, aiSuggestionDescription)}
        </Col>
        <Col className="align-self-center col-auto">
          <Button role="button" color="primary" onClick={onClickAccept}>
            <FormattedMessage
              id="app.views.a_i.manager_review.a_i_suggestion_wrapper.accept_and_replace"
              defaultMessage="Accept and replace"
            />
          </Button>
        </Col>
        <Col className="align-self-center col-auto">
          <Button role="button" color="white" onClick={onClickDiscard}>
            <FormattedMessage
              id="app.views.a_i.manager_review.a_i_suggestion_wrapper.discard"
              defaultMessage="Discard"
            />
          </Button>
        </Col>
      </Row>
    </CardBody>
  );

  if ([INPUT_TYPES.LIKERT].includes(type)) {
    return (
      <>
        {children}
        <tr>
          <td colSpan={6}>
            <div className={`ai-suggestion-wrapper-${name}`}>
              {aiSuggestionElement}
            </div>
          </td>
        </tr>
      </>
    );
  }

  return (
    <div className={`ai-suggestion-wrapper-${name}`}>
      {children}
      {aiSuggestionElement}
    </div>
  );
};

const suggestionFormatter = (
  type: string,
  aiSuggestion: any,
  aiSuggestionDescription: any
) => {
  return (
    <>
      {[INPUT_TYPES.TEXTAREA, INPUT_TYPES.RICH_TEXT_EDITOR].includes(type) &&
        !!aiSuggestion &&
        typeof aiSuggestion === 'string' &&
        aiSuggestion}
      {[INPUT_TYPES.TAGS_INPUT].includes(type) &&
        !!aiSuggestion &&
        Array.isArray(aiSuggestion) &&
        aiSuggestion.map((tag) => (
          <span key={tag.name} className="badge bg-white fs-5 mb-1 me-1">
            {tag.name}
          </span>
        ))}
      {[INPUT_TYPES.SWITCH].includes(type) &&
        typeof aiSuggestion === 'boolean' &&
        (aiSuggestion ? 'Yes' : 'No')}
      {[
        INPUT_TYPES.DROPDOWN,
        INPUT_TYPES.MULTIPLE_CHOICE,
        INPUT_TYPES.LIKERT,
      ].includes(type) &&
        !!aiSuggestionDescription && <>{aiSuggestionDescription}</>}
    </>
  );
};

const extractSuggestion = (
  singletonLikert: boolean,
  aiSuggestion: any,
  object: any,
  aiSuggestionDescription: any,
  parentValue: any
) => {
  if (singletonLikert) {
    return {
      aiSuggestionItem: aiSuggestion,
      aiSuggestionItemDescription: aiSuggestionDescription.description,
      propValue: parentValue,
    };
  }
  return {
    aiSuggestionItem: aiSuggestion?.[object.name],
    aiSuggestionItemDescription:
      aiSuggestionDescription?.[object.name]?.description,
    propValue: parentValue?.[object.name],
  };
};

const suggestionIsNotPresent = (aiSuggestionItem: any) => {
  return (
    !aiSuggestionItem ||
    aiSuggestionItem === undefined ||
    aiSuggestionItem === null
  );
};

export const aiAssistedInputWrapper = (
  input: React.ReactNode,
  _object: any,
  customProperties?: CustomProperties
) => {
  // @ts-expect-error
  const propName = input?.name;
  const inputChangeRef = customProperties?.inputChangeRef;

  const suggestionsReady = !!customProperties?.suggestions;
  const aiSuggestion = customProperties?.suggestions?.[propName]?.value;
  const aiSuggestionDescription =
    customProperties?.suggestions?.[propName]?.description;
  const singletonLikert =
    !!customProperties?.suggestions?.[propName]?.singleton;
  if (
    propName &&
    // @ts-expect-error
    input?.type &&
    [INPUT_TYPES.LIKERT].includes(
      // @ts-expect-error
      input.type
    )
  ) {
    input['subItemWrapper'] = (({
      parentProps,
      object,
      renderedElement: element,
    }) => {
      const propName = object.name;
      const parentName = parentProps?.name;
      const parentValue = parentProps?.value;
      const { aiSuggestionItem, aiSuggestionItemDescription, propValue } =
        extractSuggestion(
          singletonLikert,
          aiSuggestion,
          object,
          aiSuggestionDescription,
          parentValue
        );

      if (suggestionIsNotPresent(aiSuggestionItem)) {
        return element;
      }

      return (
        <>
          <ValidatedInputAIWrapper
            key={'suggestion-wrapper-' + propName + '-' + (parentName || '')}
            aiSuggestion={aiSuggestionItem}
            singletonLikert={singletonLikert}
            aiSuggestionDescription={aiSuggestionItemDescription}
            inputChangeRef={inputChangeRef}
            name={propName}
            value={propValue}
            parentName={parentName}
            parentValue={parentValue}
            // @ts-expect-error
            type={input.type}
            suggestionsReady={suggestionsReady}
          >
            <>{element}</>
          </ValidatedInputAIWrapper>
        </>
      );
    }) as LikertScaleInputSubItemWrapper;
  }
  return input;
};

/**
 * Used to manipulate every item of the form
 * and wrap with with an AI suggestion if available
 */

export const aiAssistedOutputWrapper = (
  output: React.ReactNode,
  customFields: Record<string, any> = {}
) => {
  // @ts-expect-error
  const propName = output?.props?.name;
  const inputChangeRef = customFields?.inputChangeRef;
  // @ts-expect-error
  const propValue = output?.props?.value;
  const suggestionsReady = !!customFields?.suggestions;
  const aiSuggestion = customFields?.suggestions?.[propName]?.value;
  const aiSuggestionDescription =
    customFields?.suggestions?.[propName]?.description;

  if (
    propName &&
    // @ts-expect-error
    output?.props?.type &&
    [
      INPUT_TYPES.TEXTAREA,
      INPUT_TYPES.RICH_TEXT_EDITOR,
      INPUT_TYPES.TAGS_INPUT,
      INPUT_TYPES.SWITCH,
      INPUT_TYPES.DROPDOWN,
      INPUT_TYPES.MULTIPLE_CHOICE,
    ].includes(
      // @ts-expect-error
      output.props.type
    )
  ) {
    return (
      <ValidatedInputAIWrapper
        key={'suggestion-wrapper-' + propName}
        aiSuggestion={aiSuggestion}
        aiSuggestionDescription={aiSuggestionDescription}
        inputChangeRef={inputChangeRef}
        name={propName}
        value={propValue}
        // @ts-expect-error
        type={output.props.type}
        suggestionsReady={suggestionsReady}
        singletonLikert={false}
      >
        {output}
      </ValidatedInputAIWrapper>
    );
  }

  return output;
};

const isAISuggestionEquals = (
  type: string,
  a: unknown,
  b: unknown
): boolean => {
  if ([INPUT_TYPES.TAGS_INPUT].includes(type)) {
    // avoid comparing the IDs of the tags, since
    // at the moment the payload doesn't return that
    const aTagNames = Array.isArray(a) ? a.map((tag) => tag.name) : [];
    const bTagNames = Array.isArray(b) ? b.map((tag) => tag.name) : [];
    return isEqual(aTagNames, bTagNames);
  } else if ([INPUT_TYPES.MULTIPLE_CHOICE].includes(type)) {
    // This is a quirk when deserializing Multiple choice.
    // Coming from the backend, when deserializing it can contains an object
    // with the name and id in it nested. Weirdly is saved anyway as key: <id>
    // in the relationship.
    // @ts-expect-error
    const aId = typeof a === 'string' ? a : a?.id;
    // @ts-expect-error
    const bId = typeof b === 'string' ? b : b?.id;
    return isEqual(aId, bId);
  }

  return isEqual(a, b);
};

export default ValidatedInputAIWrapper;
